// @flow
import React, {useState, useEffect, useReducer, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {
  Box,
  Typography,
  Checkbox,
  Divider,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useNearScreen} from '../../../../../utils/react';
import {useProductOptionGroups} from '../../../../product-option/utils';

import type {StoreProductOptionGroup} from '../../../../../entities/product-option';
import type {ProductOptionGroup} from '../../../../../entities/product';
import {mapStoreProductOptionGroupToProductOptionGroup} from '../../../utils/storeProductOptionMapper';
import {useStoreOptionActions} from './OptionsContext';

// Action types
const ACTIONS = {
  RESET_SELECTION: 'RESET_SELECTION',
  SELECT_GROUP: 'SELECT_GROUP',
  DESELECT_GROUP: 'DESELECT_GROUP',
  SELECT_OPTION: 'SELECT_OPTION',
  DESELECT_OPTION: 'DESELECT_OPTION',
  SELECT_ALL_OPTIONS: 'SELECT_ALL_OPTIONS',
};

type SelectionState = {
  selectedGroups: string[],
  selectedOptions: Array<{groupId: string, optionId: string}>,
};

type Action =
  | {type: 'RESET_SELECTION'}
  | {type: 'SELECT_GROUP', payload: {groupId: string}}
  | {type: 'DESELECT_GROUP', payload: {groupId: string}}
  | {type: 'SELECT_OPTION', payload: {groupId: string, optionId: string}}
  | {type: 'DESELECT_OPTION', payload: {groupId: string, optionId: string}}
  | {
      type: 'SELECT_ALL_OPTIONS',
      payload: {groupId: string, optionIds: string[]},
    };

const useStyles = makeStyles(theme => ({
  modalPaper: {
    padding: '.75rem .75rem 0 .75rem',
    maxWidth: '700px',

    [theme.breakpoints.up('sm')]: {
      padding: '1rem 1rem 0 1rem',
      width: '90vw',
    },
  },

  modalContent: {
    width: '100%',
    maxWidth: 700,
    minWidth: 300,
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    maxHeight: '600px',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.grey800,
    marginBlock: '.75rem',
    textAlign: 'center',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
  optionItem: {
    padding: '.5rem',
    border: `2px solid ${colors.grey200}`,
    borderRadius: '1rem',
    backgroundColor: colors.white,
    flexDirection: 'column',
    alignItems: 'flex-start',
    overflow: 'hidden',

    '&[data-selected="true"]': {
      borderColor: colors.purple3,
      backgroundColor: colors.purple50,
    },

    [theme.breakpoints.up('md')]: {
      padding: '.75rem',
    },
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  optionGroupLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexGrow: 1,
  },
  optionName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  optionDetails: {
    fontSize: '0.875rem',
    color: colors.grey700,
    marginBottom: theme.spacing(1),
  },
  optionDescription: {
    fontSize: '0.75rem',
    color: colors.grey600,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionMeta: {
    fontSize: '0.75rem',
    color: colors.grey500,
  },
  detailsButton: {
    fontSize: '0.75rem',
    color: colors.purple3,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  deleteButton: {
    fontSize: '0.75rem',
    color: colors.red500,
    textDecoration: 'none',
    cursor: 'pointer',
    marginLeft: theme.spacing(2),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingBlock: theme.spacing(2),
    borderTop: `1px solid ${colors.grey200}`,
    backgroundColor: '#ffffffcc',
    zIndex: 1,
    backdropFilter: 'blur(12px)',
    position: 'sticky',
    bottom: 0,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1rem',
  },
  configuredBadge: {
    backgroundColor: colors.lightPurple,
    color: colors.purple,
    fontSize: '0.7rem',
    height: 18,
    marginLeft: '1rem',
  },
  selectAllButton: {
    fontSize: '0.75rem',
    color: colors.purple3,
    cursor: 'pointer',
  },
  expandButton: {
    padding: theme.spacing(0.5),
    marginLeft: 'auto',
  },
  createButton: {
    padding: '.5rem 2rem',
    fontWeight: '600',
    textTransform: 'none',
  },
  optionCheckbox: {
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(1),
  },
  optionsContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      maxHeight: '600px',
    },
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: colors.grey600,
  },
  valuesChips: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  valueChip: {
    height: 20,
    fontSize: '0.75rem',
    backgroundColor: colors.grey200,
    color: colors.grey700,
  },
  groupOptionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '.25rem .75rem .25rem .25rem',
    backgroundColor: colors.grey200,
    color: colors.grey700,
    borderRadius: '.5rem',
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.grey100,
    },
    '&[data-selected="true"]': {
      backgroundColor: colors.lightPurple,
      color: colors.purple,
    },
  },
}));

type Props = {
  open: boolean,
  onClose: () => void,
  onAddOptions: (selectedOptions: ProductOptionGroup[]) => void,
  configuredGroupIds: string[],
};

function selectionReducer(
  state: SelectionState,
  action: Action
): SelectionState {
  switch (action.type) {
    case ACTIONS.RESET_SELECTION:
      return {
        selectedGroups: [],
        selectedOptions: [],
      };

    case ACTIONS.SELECT_GROUP:
      return {
        ...state,
        selectedGroups: [...state.selectedGroups, action.payload.groupId],
        selectedOptions: state.selectedOptions.filter(
          opt => opt.groupId !== action.payload.groupId
        ),
      };

    case ACTIONS.DESELECT_GROUP:
      return {
        ...state,
        selectedGroups: state.selectedGroups.filter(
          id => id !== action.payload.groupId
        ),
        selectedOptions: state.selectedOptions.filter(
          opt => opt.groupId !== action.payload.groupId
        ),
      };

    case ACTIONS.SELECT_OPTION:
      return {
        ...state,
        selectedOptions: [
          ...state.selectedOptions,
          {groupId: action.payload.groupId, optionId: action.payload.optionId},
        ],
      };

    case ACTIONS.DESELECT_OPTION:
      return {
        ...state,
        selectedOptions: state.selectedOptions.filter(
          opt =>
            !(
              opt.groupId === action.payload.groupId &&
              opt.optionId === action.payload.optionId
            )
        ),
      };

    case ACTIONS.SELECT_ALL_OPTIONS:
      const filteredOptions = state.selectedOptions.filter(
        opt => opt.groupId !== action.payload.groupId
      );
      const newOptions = action.payload.optionIds.map(optionId => ({
        groupId: action.payload.groupId,
        optionId,
      }));
      return {
        ...state,
        selectedOptions: [...filteredOptions, ...newOptions],
      };

    default:
      return state;
  }
}

export default function SavedOptionsModal({
  open,
  onClose,
  onAddOptions,
  configuredGroupIds,
}: Props) {
  const [selectionState, dispatch] = useReducer(selectionReducer, {
    selectedGroups: [],
    selectedOptions: [],
  });

  const {selectedGroups, selectedOptions} = selectionState;
  const classes = useStyles();

  const {addBatchStoreGroupOptions} = useStoreOptionActions();

  const {
    items: savedOptionGroups,
    loading,
    loadingMore,
    hasMore,
    loadMore,
  } = useProductOptionGroups();

  const {isNearScreen, fromRef} = useNearScreen({
    distance: '100px',
    once: false,
  });

  useEffect(() => {
    if (isNearScreen && hasMore && !loadingMore) {
      loadMore();
    }
  }, [isNearScreen, hasMore, loadingMore, loadMore]);

  useEffect(() => {
    if (open) {
      dispatch({type: ACTIONS.RESET_SELECTION});
    }
  }, [open]);

  useEffect(() => {
    addBatchStoreGroupOptions(savedOptionGroups);
  }, [addBatchStoreGroupOptions, savedOptionGroups]);

  const handleGroupSelect = useCallback((groupId: string, checked: boolean) => {
    if (checked) {
      dispatch({type: ACTIONS.SELECT_GROUP, payload: {groupId}});
    } else {
      dispatch({type: ACTIONS.DESELECT_GROUP, payload: {groupId}});
    }
  }, []);

  const handleOptionSelect = useCallback(
    (groupId: string, optionId: string, checked: boolean) => {
      if (checked) {
        dispatch({type: ACTIONS.SELECT_OPTION, payload: {groupId, optionId}});
      } else {
        dispatch({
          type: ACTIONS.DESELECT_OPTION,
          payload: {groupId, optionId},
        });
      }
    },
    []
  );

  const handleSelectAllOptions = useCallback(
    (groupId: string, group: StoreProductOptionGroup) => {
      const optionIds = group.options?.map(opt => opt.id) || [];
      dispatch({
        type: ACTIONS.SELECT_ALL_OPTIONS,
        payload: {groupId, optionIds},
      });
    },
    []
  );

  const isGroupSelected = (groupId: string) => selectedGroups.includes(groupId);

  const hasSelectedItems = selectedOptions.length > 0;

  const handleAddSelected = () => {
    const optionsToAdd = [];

    const groupedOptions = selectedOptions.reduce(
      (acc, {groupId, optionId}) => {
        if (!acc[groupId]) acc[groupId] = [];
        acc[groupId].push(optionId);
        return acc;
      },
      {}
    );

    Object.entries(groupedOptions).forEach(([groupId, optionIds]) => {
      const group = savedOptionGroups.find(g => g.id === groupId);
      if (group && !configuredGroupIds.includes(groupId)) {
        const selectedGroupOptions =
          group.options?.filter(opt => optionIds.includes(opt.id)) || [];

        if (selectedGroupOptions.length > 0) {
          optionsToAdd.push({
            group,
            selectedOptions: selectedGroupOptions,
          });
        }
      }
    });

    const optionsGroups = optionsToAdd.map(
      ({group, selectedOptions}, index) => {
        const modifiedGroup = {
          ...group,
          options: selectedOptions,
        };

        return mapStoreProductOptionGroupToProductOptionGroup(modifiedGroup, {
          number: index,
          fieldType: 'MULTI_OPTION',
          config: {
            presentationType: 'LISTING',
          },
          isNew: true,
        });
      }
    );

    onAddOptions(optionsGroups);
    dispatch({type: ACTIONS.RESET_SELECTION});
    onClose();
  };

  return (
    <CustomModal
      open={open}
      handleCloseModal={onClose}
      classes={{paper: classes.modalPaper}}
    >
      <Box className={classes.modalContent}>
        <Typography className={classes.title}>Opciones Guardadas</Typography>

        {loading && savedOptionGroups.length === 0 ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
            <Typography style={{marginLeft: '8px'}}>
              Cargando opciones...
            </Typography>
          </Box>
        ) : (
          <Box className={classes.optionsList}>
            {savedOptionGroups.length === 0 ? (
              <Box style={{textAlign: 'center', padding: '2rem'}}>
                <Typography variant="h6" style={{marginBottom: '1rem'}}>
                  No hay grupos de opciones disponibles
                </Typography>
                <CustomButton
                  component={Link}
                  to="/product-option"
                  className={classes.createButton}
                  variant="contained"
                  color="primary"
                >
                  Crear Grupo de Opciones
                </CustomButton>
              </Box>
            ) : (
              savedOptionGroups.map(group => {
                const isConfigured = configuredGroupIds.includes(group.id);
                const groupSelected = isGroupSelected(group.id);
                const hasSelectedOptionsInGroup = selectedOptions.some(
                  opt => opt.groupId === group.id
                );
                const selectedOptionIds = selectedOptions
                  .filter(opt => opt.groupId === group.id)
                  .map(opt => opt.optionId);

                return (
                  <MemoizedGroupItem
                    key={group.id}
                    group={group}
                    isConfigured={isConfigured}
                    groupSelected={groupSelected}
                    hasSelectedOptionsInGroup={hasSelectedOptionsInGroup}
                    selectedOptionIds={selectedOptionIds}
                    onGroupSelect={handleGroupSelect}
                    onOptionSelect={handleOptionSelect}
                    onSelectAllOptions={handleSelectAllOptions}
                  />
                );
              })
            )}

            {loadingMore && (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={20} />
                <Typography style={{marginLeft: '8px', fontSize: '0.875rem'}}>
                  Cargando más...
                </Typography>
              </Box>
            )}

            {/* Infinite scroll trigger */}
            <div ref={fromRef} style={{height: '1px'}} />

            {!loading && savedOptionGroups.length === 0 && (
              <Box className={classes.emptyState}>
                <Typography>No se encontraron opciones guardadas</Typography>
              </Box>
            )}
          </Box>
        )}
        <Box className={classes.actionsContainer}>
          <CustomButton
            className={classes.addButton}
            onClick={handleAddSelected}
            disabled={!hasSelectedItems}
            fullWidth
          >
            Agregar Opciones Seleccionadas
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>
  );
}

type GroupItemProps = {
  group: StoreProductOptionGroup,
  isConfigured: boolean,
  groupSelected: boolean,
  hasSelectedOptionsInGroup: boolean,
  selectedOptionIds: string[],
  onGroupSelect: (groupId: string, checked: boolean) => void,
  onOptionSelect: (groupId: string, optionId: string, checked: boolean) => void,
  onSelectAllOptions: (groupId: string, group: StoreProductOptionGroup) => void,
};

function GroupItem({
  group,
  isConfigured,
  groupSelected,
  hasSelectedOptionsInGroup,
  selectedOptionIds,
  onGroupSelect,
  onOptionSelect,
  onSelectAllOptions,
}: GroupItemProps) {
  const classes = useStyles();

  const [expanded, setExpanded] = useState(false);

  const canSelectOptions = groupSelected && !isConfigured;

  const handleGroupSelect = (groupId: string, checked: boolean) => {
    onGroupSelect(groupId, checked);
    setExpanded(checked);
  };

  const handleExpandToggle = () => {
    setExpanded(!expanded);
  };

  const visibleOptions = group.options?.slice(0, 3) || [];

  const optionsLength = group.options?.length || 0;
  const remainingCount = Math.max(0, optionsLength - 3);

  return (
    <Box
      key={group.id}
      className={classes.optionItem}
      data-selected={groupSelected || hasSelectedOptionsInGroup}
    >
      <Box className={classes.optionHeader}>
        <label
          className={classes.optionGroupLabel}
          style={{marginLeft: !isConfigured ? '-.75rem' : '0'}}
        >
          {!isConfigured && (
            <Checkbox
              checked={groupSelected}
              onChange={e => handleGroupSelect(group.id, e.target.checked)}
              color="primary"
              disabled={isConfigured}
            />
          )}
          <Typography className={classes.optionName}>
            {group.name}
            {isConfigured && (
              <Chip
                label="Configurado"
                size="small"
                className={classes.configuredBadge}
              />
            )}
          </Typography>
        </label>
        {groupSelected && !isConfigured && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            style={{gap: '.5rem'}}
          >
            <IconButton
              className={classes.expandButton}
              onClick={handleExpandToggle}
              size="small"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        )}
      </Box>

      {group.description && (
        <Typography className={classes.optionDescription}>
          Descripción: {group.description}
        </Typography>
      )}

      <Divider style={{margin: '8px 0'}} />

      {(!expanded || isConfigured) && (
        <Box className={classes.valuesChips}>
          {visibleOptions.map(opt => (
            <Chip
              key={opt.id}
              label={opt.name}
              size="small"
              className={classes.valueChip}
            />
          ))}
          {remainingCount > 0 && (
            <Chip
              label={`+${remainingCount} más`}
              size="small"
              className={classes.valueChip}
            />
          )}
        </Box>
      )}

      <Collapse in={expanded && canSelectOptions}>
        <Box display="flex" justifyContent="space-between">
          <Typography className={classes.optionDetails}>
            Opciones ({group.options?.length})
          </Typography>
          <CustomButton
            className={classes.selectAllButton}
            size="small"
            onClick={() => onSelectAllOptions(group.id, group)}
          >
            Seleccionar Todas
          </CustomButton>
        </Box>
        <Box display="flex" style={{gap: '.5rem', flexWrap: 'wrap'}}>
          {group.options?.map(opt => {
            const optionId = opt.id;
            const isSelected = selectedOptionIds.includes(optionId);

            return (
              <OptionItem
                key={optionId}
                option={opt}
                optionId={optionId}
                isSelected={isSelected}
                groupId={group.id}
                onOptionSelect={onOptionSelect}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

const OptionItem = React.memo(function OptionItem({
  option,
  optionId,
  isSelected,
  groupId,
  onOptionSelect,
}: {
  option: any,
  optionId: string,
  isSelected: boolean,
  groupId: string,
  onOptionSelect: (groupId: string, optionId: string, checked: boolean) => void,
}) {
  const classes = useStyles();

  const handleChange = (e: any) => {
    onOptionSelect(groupId, optionId, e.target.checked);
  };

  return (
    <label className={classes.groupOptionItem} data-selected={isSelected}>
      <Checkbox
        className={classes.optionCheckbox}
        checked={isSelected}
        onChange={handleChange}
        color="primary"
        size="small"
      />
      <Typography variant="body2">{option.name}</Typography>
    </label>
  );
});

const MemoizedGroupItem = React.memo(GroupItem, (prevProps, nextProps) => {
  return (
    prevProps.group.id === nextProps.group.id &&
    prevProps.isConfigured === nextProps.isConfigured &&
    prevProps.groupSelected === nextProps.groupSelected &&
    prevProps.hasSelectedOptionsInGroup ===
      nextProps.hasSelectedOptionsInGroup &&
    JSON.stringify(prevProps.selectedOptionIds) ===
      JSON.stringify(nextProps.selectedOptionIds)
  );
});
